import { Box, Text, TextButton, Tooltip } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { ElementStatus, usePreviousState } from '@wix/rules-panel-ui';
import { InfoCircleSmall, StatusAlertFilledSmall, StatusCompleteFilledSmall, StatusWarningFilledSmall } from '@wix/wix-ui-icons-common';
import React, { useCallback, useEffect, useMemo } from 'react';
import { BiCtaNames, BiNotificationTypes } from '../../../../../bi/types';
import { dataHooks } from '../../../../../exports/testkit';
import { useBiLogger } from '../../../../../hooks/use-bi-logger';
import { getDisabledButtonTooltipTranslationKeySuffix } from '../../utils/get-disabled-button-tooltip-translation-key-suffix';
import { useAltTextScreenContext } from '../alt-text-screen/alt-text-screen-wrapper';
export const AltTextSummary = () => {
  const biLogger = useBiLogger();
  const {
    t
  } = useTranslation();
  const {
    state: {
      altRulesProps,
      altTextDraftDataMap,
      isGeneratingAltTextSuggestions
    },
    actions: {
      handleGenerateWithAiClick,
      setAltRulesProps,
      setIsSortedByUnsupported
    }
  } = useAltTextScreenContext();
  const getStatus = useCallback(el => {
    const altTextData = altTextDraftDataMap == null ? void 0 : altTextDraftDataMap.get(el.id);
    const sessionStatus = (altTextData == null ? void 0 : altTextData.status) ?? el.status;
    return sessionStatus;
  }, [altTextDraftDataMap]);
  const generatedCount = altRulesProps.filter(el => getStatus(el) === ElementStatus.GeneratedAll).length;
  const failedToGenerate = altRulesProps.filter(el => el.isAiSupported && getStatus(el) === ElementStatus.Warning);
  const failedCount = failedToGenerate.length;
  const unsupportedElements = altRulesProps.filter(el => getStatus(el) === ElementStatus.Failed && !el.isAiSupported);
  const unsupportedCount = unsupportedElements.length;
  const disabledTooltipTranslationKeySuffix = getDisabledButtonTooltipTranslationKeySuffix(unsupportedElements);
  const taskNotificationPayload = useMemo(() => ({
    failed: failedCount,
    generated: generatedCount,
    unsupportedTotal: unsupportedCount
  }), [failedCount, generatedCount, unsupportedCount]);
  const prevIsGenerating = usePreviousState(isGeneratingAltTextSuggestions);
  const prevGeneratedCount = usePreviousState(generatedCount);
  useEffect(() => {
    const justFinishedGenerating = prevIsGenerating && !isGeneratingAltTextSuggestions;
    const hasNewResults = generatedCount > 0 && generatedCount !== prevGeneratedCount;
    if (justFinishedGenerating && hasNewResults) {
      biLogger.taskNotificationView({
        notificationType: BiNotificationTypes.altTextGeneratedResultsStatus,
        payload: taskNotificationPayload
      });
    }
  }, [biLogger, isGeneratingAltTextSuggestions, prevIsGenerating, generatedCount, prevGeneratedCount, taskNotificationPayload]);
  const handleSortUnsupported = () => {
    const unsupportedItems = altRulesProps.filter(item => !item.isAiSupported && item.status === ElementStatus.Failed);
    const supportedItems = altRulesProps.filter(item => item.isAiSupported);
    const sortedAltRules = [...unsupportedItems, ...supportedItems];
    setIsSortedByUnsupported(true);
    setAltRulesProps(sortedAltRules);
  };
  return /*#__PURE__*/React.createElement(Box, {
    backgroundColor: "B60",
    border: "1px solid",
    borderColor: "B40",
    borderRadius: "4px",
    boxSizing: "border-box",
    dataHook: dataHooks.altTextSummary,
    direction: "vertical",
    gap: "SP1",
    padding: "SP2",
    width: "100%"
  }, generatedCount > 0 && /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "middle",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    color: "G10",
    dataHook: dataHooks.altTextSummaryGeneratedIcon,
    paddingRight: "SP1"
  }, /*#__PURE__*/React.createElement(StatusCompleteFilledSmall, null)), /*#__PURE__*/React.createElement(Text, {
    dataHook: dataHooks.altTextSummaryGeneratedText,
    maxLines: 1,
    size: "tiny",
    textAlign: "center",
    weight: "bold",
    ellipsis: true
  }, t('accessibility-wizard.alt-text.summary.generated', {
    generatedCount
  }))), failedCount > 0 && /*#__PURE__*/React.createElement(Box, {
    verticalAlign: "middle"
  }, /*#__PURE__*/React.createElement(Box, {
    color: "Y10",
    dataHook: dataHooks.altTextSummaryFailedIcon,
    paddingRight: "SP1"
  }, /*#__PURE__*/React.createElement(StatusWarningFilledSmall, null)), /*#__PURE__*/React.createElement(Text, {
    dataHook: dataHooks.altTextSummaryFailedText,
    maxLines: 1,
    size: "tiny",
    weight: "bold",
    ellipsis: true
  }, t('accessibility-wizard.alt-text.summary.failed', {
    failedCount
  })), /*#__PURE__*/React.createElement(Box, {
    paddingLeft: "SP1"
  }, /*#__PURE__*/React.createElement(TextButton, {
    dataHook: dataHooks.altTextSummaryFailedButton,
    size: "tiny",
    skin: "dark",
    underline: "always",
    ellipsis: true,
    onClick: () => {
      biLogger.taskNotificationCtaClick({
        ctaName: BiCtaNames.TryAgain,
        notificationType: BiNotificationTypes.altTextGeneratedResultsStatus,
        payload: taskNotificationPayload
      });
      handleGenerateWithAiClick(failedToGenerate);
    }
  }, t('accessibility-wizard.alt-text.summary.failed.try-again')))), unsupportedCount > 0 && /*#__PURE__*/React.createElement(Box, {
    width: "228px"
  }, /*#__PURE__*/React.createElement(Box, {
    color: "R10",
    dataHook: dataHooks.altTextSummaryUnsupportedIcon,
    paddingRight: "SP1"
  }, /*#__PURE__*/React.createElement(StatusAlertFilledSmall, null)), /*#__PURE__*/React.createElement(Box, {
    flexWrap: "wrap",
    verticalAlign: "middle"
  }, /*#__PURE__*/React.createElement(Box, {
    maxWidth: "204px"
  }, /*#__PURE__*/React.createElement(Text, {
    dataHook: dataHooks.altTextSummaryUnsupportedText,
    maxLines: 1,
    size: "tiny",
    weight: "bold",
    ellipsis: true
  }, t('accessibility-wizard.alt-text.summary.unsupported', {
    unsupportedCount
  })), /*#__PURE__*/React.createElement(Tooltip, {
    content: t(`accessibility-wizard.alt-text.generate-with-ai-button.tooltip.description.${disabledTooltipTranslationKeySuffix}`),
    dataHook: dataHooks.altTextSummaryUnsupportedTooltip,
    maxWidth: 182,
    placement: "top",
    size: "small",
    onShow: () => {
      disabledTooltipTranslationKeySuffix && biLogger.tooltipView({
        tooltipName: disabledTooltipTranslationKeySuffix
      });
    }
  }, /*#__PURE__*/React.createElement(Box, {
    paddingLeft: "1px"
  }, /*#__PURE__*/React.createElement(InfoCircleSmall, null)))), /*#__PURE__*/React.createElement(Box, {
    paddingLeft: "4px"
  }, /*#__PURE__*/React.createElement(TextButton, {
    dataHook: dataHooks.altTextSummaryUnsupportedButton,
    size: "tiny",
    skin: "dark",
    underline: "always",
    ellipsis: true,
    onClick: () => {
      biLogger.taskNotificationCtaClick({
        ctaName: BiCtaNames.SortFirst,
        notificationType: BiNotificationTypes.altTextGeneratedResultsStatus,
        payload: taskNotificationPayload
      });
      handleSortUnsupported();
    }
  }, t('accessibility-wizard.alt-text.summary.unsupported.sort'))))));
};
//# sourceMappingURL=alt-text-summary.js.map